import React, { useState, useEffect, useRef, useCallback } from 'react';
import FileDropZone from '../shared/FileDropZone';
import ReadingProgress from '../ui/ReadingProgress';
import { announceReadingStarted } from '../ui/ReadingTray';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faUpload,
  faPaperPlane,
  faPlus,
  faTrash,
  faFile,
  faChevronRight,
  faBuilding,
  faGlobe,
  faBoxOpen,
  faChartLine,
  faAddressBook,
  faInfoCircle,
  faRobot,
  faUser,
  faSpinner,
  faCloudUploadAlt,
  faTimes,
  faFileAlt,
  faComments,
  faFolder,
  faList,
  faThLarge,
  faHome,
  faCheck,
  faCheckCircle,
  faBrain,
  faSearch,
  faExclamationTriangle,
  faEye,
  faPaperclip,
  faDownload,
  faRedo,
} from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import HMSNavbarWrapper from '../ui/HMSNavbarWrapper';
import Footer from '../ui/Footer';
import withAuth from '../../utils/withAuth';
import knowledgeHubService from '../../services/knowledgeHubService';
import { createCrmPartnerFromDocument, linkDocumentToPartner, downloadDocument } from '../../services/knowledgeHubService';
import axiosInstance from '../../utils/axiosConfig';
import { lookupNames } from '../../services/documentNamingService';
import { crmService } from '../../services/crmService';
import PdfPreviewModal from '../shared/PdfPreviewModal';
import { extractApiError } from '../../utils/errorMessages';
import logo from '../../assets/images/IMT_logo.png';
import './KnowledgeHub.css';
import { buildFolders, folderOf, isStillReading, CATEGORY_KEYS, UNSORTED } from './knowledgeHubFolders';

const CLASSIFICATION_META = {
  product: { label: 'Product Info', icon: faBoxOpen },
  market: { label: 'Market Info', icon: faChartLine },
  partner: { label: 'Partner Info', icon: faAddressBook },
  general: { label: 'General Info', icon: faInfoCircle },
};

// The General area's folders: the four categories, plus documents that have
// no category yet. Upload pickers keep using CLASSIFICATION_META only.
const GENERAL_FOLDER_META = {
  ...CLASSIFICATION_META,
  [UNSORTED]: { label: 'Not sorted yet', icon: faExclamationTriangle },
};

const TEXT_EXTENSIONS = ['txt', 'md', 'csv', 'log'];

const IMAGE_MIME_TYPES = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function KnowledgeHub() {
  // Data state
  const [documents, setDocuments] = useState([]);
  const [knowledgeDisplayNames, setKnowledgeDisplayNames] = useState({});
  const [chatSessions, setChatSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Whether the processing modal was reached via New Entry (swaps its copy)
  const [entryFlow, setEntryFlow] = useState(false);
  const [browserPath, setBrowserPath] = useState([]);
  const [viewMode, setViewMode] = useState('list');

  // Chat state
  const [currentSession, setCurrentSession] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const chatEndRef = useRef(null);
  const chatMessagesRef = useRef(null);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  /* Both file doors used to drop an oversize file on the floor in silence.
     One message, shown wherever the file was refused. */
  const [dropError, setDropError] = useState('');

  const KH_MAX_BYTES = 50 * 1024 * 1024;

  const takeFile = (file, setter) => {
    if (!file) {
      setDropError('No file was detected.');
      return;
    }
    if (file.size > KH_MAX_BYTES) {
      setDropError(`"${file.name}" is larger than the 50MB limit.`);
      return;
    }
    setDropError('');
    setter(file);
  };
  const [uploadClassification, setUploadClassification] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingDocId, setProcessingDocId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingStep, setProcessingStep] = useState(0);
  const [processingDone, setProcessingDone] = useState(false);
  // Bumped on every read-again, so watching restarts even for the same document.
  const [processingRun, setProcessingRun] = useState(0);
  // { id, message } when a read-again could not start
  const [readAgainError, setReadAgainError] = useState(null);
  const [readAgainBusyId, setReadAgainBusyId] = useState(null);

  // CRM partner prompt state (shown when a partner doc has no CRM match)
  const [crmPromptState, setCrmPromptState] = useState('idle'); // 'idle' | 'prompt' | 'form' | 'submitting' | 'done' | 'error'
  const [crmFormData, setCrmFormData] = useState({ legal_entity_name: '', trading_short_name: '' });
  const [crmFormError, setCrmFormError] = useState('');

  // Link-to-partner correction state (for general-classified documents)
  const [linkPartnerState, setLinkPartnerState] = useState('idle');
  const [linkPartnerSearch, setLinkPartnerSearch] = useState('');
  const [linkPartnerResults, setLinkPartnerResults] = useState([]);
  const [linkPartnerSelected, setLinkPartnerSelected] = useState(null);
  const [linkPartnerLoading, setLinkPartnerLoading] = useState(false);
  const [linkPartnerDebouncedSearch, setLinkPartnerDebouncedSearch] = useState('');

  // In-app preview state ({ id, name, mimeType, url, loading })
  const [previewDoc, setPreviewDoc] = useState(null);

  // New entry state
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [newEntryText, setNewEntryText] = useState('');
  const [newEntryFile, setNewEntryFile] = useState(null);
  const [newEntryClassification, setNewEntryClassification] = useState('');
  const [creatingEntry, setCreatingEntry] = useState(false);
  const [newEntryError, setNewEntryError] = useState('');

  // Attach-to-chat state
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachedDocs, setAttachedDocs] = useState([]);
  const [attachSearch, setAttachSearch] = useState('');

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  const fileInputRef = useRef(null);
  const newEntryFileInputRef = useRef(null);
  const linkPartnerDropdownRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Every count and folder is built from this one list (see knowledgeHubFolders).
      const [docsRes, sessionsRes] = await Promise.all([
        knowledgeHubService.listDocuments(),
        knowledgeHubService.listChatSessions(),
      ]);
      const docs = docsRes.results || docsRes || [];
      setDocuments(docs);
      setChatSessions(sessionsRes.results || sessionsRes || []);
      
      try {
        const docIds = docs
          .map(d => d?.docuware_document_id)
          .filter(Boolean)
          .map(String);
        if (docIds.length > 0) {
          const map = await lookupNames(docIds, 'knowledge_hub');
          setKnowledgeDisplayNames(map || {});
        }
      } catch (lookupErr) {
        console.warn('Failed to lookup AI display names for knowledge hub (non-fatal):', lookupErr);
      }
    } catch (err) {
      console.error('Failed to load Knowledge Hub data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Warm the chat LLM on open (and stamp the activity flag so the work-hours
  // ping keeps it resident). Guarded + debounced server-side; fire-and-forget.
  useEffect(() => {
    axiosInstance.post('/api/knowledge-hub/warm-llm/').catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (chatMessages.length > 0 && chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Coming back to a document still being read (from the app-wide tray, or a
  // refresh): reopen its progress window instead of losing sight of it.
  useEffect(() => {
    const reading = new URLSearchParams(window.location.search).get('reading');
    const id = reading && Number(reading);
    if (!id) return;
    setEntryFlow(false);
    setProcessingStep(1);
    setProcessingDone(false);
    setProcessingStatus({ status: 'processing' });
    setProcessingDocId(id);
    setShowUploadModal(true);
    window.history.replaceState(null, '', window.location.pathname);
  }, [window.location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll processing status
  useEffect(() => {
    if (!processingDocId) return;
    setProcessingStep(1);
    setProcessingDone(false);
    let failedChecks = 0;
    const interval = setInterval(async () => {
      try {
        const status = await knowledgeHubService.getProcessingStatus(processingDocId);
        setProcessingStatus(status);
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          // Animate through remaining steps before showing the result card
          const finalStep = status.status === 'completed' ? 4 : -1;
          if (status.status === 'completed') {
            // Briefly show each remaining step so the user sees progress
            setProcessingStep(2);
            setTimeout(() => setProcessingStep(3), 600);
            setTimeout(() => {
              setProcessingStep(finalStep);
              setProcessingDone(true);
              loadData();
              // If this is an unmatched partner doc, trigger the CRM prompt
              if (
                status.classification === 'partner' &&
                status.storage_area === 'general' &&
                status.partner_name
              ) {
                setCrmFormData({ legal_entity_name: status.partner_name, trading_short_name: '' });
                setCrmFormError('');
                setCrmPromptState('prompt');
              }
            }, 1200);
          } else {
            setProcessingStep(finalStep);
            setProcessingDone(true);
            loadData();
          }
        }
        failedChecks = 0;
      } catch {
        // One missed check used to stop checking for good, leaving the modal
        // spinning on a document that finished. Keep going through a blip.
        failedChecks += 1;
        if (failedChecks >= 15) {
          clearInterval(interval);
          setProcessingStep(-1);
          setProcessingDone(true);
        }
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [processingDocId, processingRun, loadData]);

  // The steps follow what the server reports. They used to advance on a
  // three-second timer, so a scan still being read showed "Indexing for search".
  useEffect(() => {
    if (!processingDocId || !processingStatus) return;
    if (processingStatus.status !== 'processing' && processingStatus.status !== 'pending') return;
    const stage = processingStatus.progress?.stage;
    const step = { queued: 1, reading: 1, analysing: 2, indexing: 3, filing: 3 }[stage];
    if (step !== undefined) setProcessingStep(step);
  }, [processingDocId, processingStatus]);

  // Debounce the browser toolbar search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce the link-partner search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setLinkPartnerDebouncedSearch(linkPartnerSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [linkPartnerSearch]);

  // Fetch CRM partners when debounced search changes
  useEffect(() => {
    if (linkPartnerDebouncedSearch.trim().length < 2) {
      setLinkPartnerResults([]);
      return;
    }
    let cancelled = false;
    const fetchPartners = async () => {
      setLinkPartnerLoading(true);
      try {
        const results = await crmService.searchProfilesByName(linkPartnerDebouncedSearch);
        if (cancelled) return;
        const approved = results
          .filter((p) => (p.approval_status || '').toString().toLowerCase() === 'approved' || p.approval_status === undefined)
          .map((p) => ({
            id: p.id,
            legal_entity_name: p.legal_entity_name,
            trading_short_name: p.trading_short_name,
          }));
        setLinkPartnerResults(approved);
      } catch {
        if (!cancelled) setLinkPartnerResults([]);
      } finally {
        if (!cancelled) setLinkPartnerLoading(false);
      }
    };
    fetchPartners();
    return () => { cancelled = true; };
  }, [linkPartnerDebouncedSearch]);

  // Click outside to close link-partner dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        linkPartnerDropdownRef.current &&
        !linkPartnerDropdownRef.current.contains(event.target)
      ) {
        if (linkPartnerState === 'searching') setLinkPartnerState('idle');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [linkPartnerState]);

  // Derive filtered docs from browserPath
  const browsedDocs = (() => {
    let docs = documents;
    if (browserPath.length === 0) return docs;
    if (browserPath[0] === 'crm') {
      docs = docs.filter((d) => folderOf(d).area === 'crm');
      if (browserPath.length >= 2) {
        docs = docs.filter((d) => folderOf(d).key === browserPath[1].partnerId);
      }
    } else if (browserPath[0] === 'general') {
      docs = docs.filter((d) => folderOf(d).area === 'general');
      if (browserPath.length >= 2) {
        docs = docs.filter((d) => folderOf(d).key === browserPath[1]);
      }
    }
    return docs;
  })();

  const filteredDocs = debouncedSearch
    ? browsedDocs.filter((doc) => {
        const q = debouncedSearch.toLowerCase();
        return (
          doc.original_filename?.toLowerCase().includes(q) ||
          doc.title?.toLowerCase().includes(q) ||
          doc.ai_summary?.toLowerCase().includes(q) ||
          doc.partner_name?.toLowerCase().includes(q) ||
          doc.classification?.toLowerCase().includes(q)
        );
      })
    : browsedDocs;

  const isAtDocLevel =
    (browserPath[0] === 'crm' && browserPath.length === 2) ||
    (browserPath[0] === 'general' && browserPath.length === 2);

  // Upload handling
  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadProgress(0);
    setProcessingStep(0);
    setProcessingDone(false);
    resetCrmPrompt();
    try {
      const result = await knowledgeHubService.uploadDocument(uploadFile, setUploadProgress, uploadClassification);
      setProcessingDocId(result.id);
      setProcessingStatus({ status: 'pending' });
      announceReadingStarted();
      setUploadFile(null);
      setUploadClassification('');
    } catch (err) {
      console.error('Upload failed:', err);
      setProcessingStep(-1);
      setProcessingStatus({ status: 'failed', error: 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const closeNewEntryModal = () => {
    setShowNewEntryModal(false);
    setNewEntryText('');
    setNewEntryFile(null);
    setNewEntryClassification('');
    setNewEntryError('');
  };

  // Create a knowledge entry from scratch (note + optional attachment), then
  // hand off to the upload modal's existing processing-status flow.
  const handleCreateEntry = async () => {
    if (!newEntryText.trim() && !newEntryFile) return;
    setCreatingEntry(true);
    setNewEntryError('');
    try {
      const result = await knowledgeHubService.createEntry(
        newEntryText,
        newEntryFile,
        newEntryClassification
      );
      closeNewEntryModal();
      setProcessingStep(0);
      setProcessingDone(false);
      resetCrmPrompt();
      setProcessingDocId(result.id);
      setProcessingStatus({ status: 'pending' });
      announceReadingStarted();
      setEntryFlow(true);
      setShowUploadModal(true);
    } catch (err) {
      console.error('Entry creation failed:', err);
      setNewEntryError('Failed to create entry. Please try again.');
    } finally {
      setCreatingEntry(false);
    }
  };

  const handleCrmFormSubmit = async () => {
    const name = crmFormData.legal_entity_name.trim();
    if (!name) {
      setCrmFormError('Legal entity name is required.');
      return;
    }
    setCrmFormError('');
    setCrmPromptState('submitting');
    try {
      await createCrmPartnerFromDocument({
        document_id: processingDocId,
        legal_entity_name: name,
        trading_short_name: crmFormData.trading_short_name.trim(),
      });
      setProcessingStatus((prev) => ({
        ...prev,
        classification: 'partner',
        storage_area: 'crm',
        partner_name: name,
      }));
      setCrmPromptState('done');
      setLinkPartnerState('done');
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to create CRM partner. Please try again.';
      setCrmFormError(msg);
      setCrmPromptState('error');
    }
  };

  const resetCrmPrompt = () => {
    setCrmPromptState('idle');
    setCrmFormData({ legal_entity_name: '', trading_short_name: '' });
    setCrmFormError('');
    setLinkPartnerState('idle');
    setLinkPartnerSearch('');
    setLinkPartnerResults([]);
    setLinkPartnerSelected(null);
    setLinkPartnerLoading(false);
    setLinkPartnerDebouncedSearch('');
  };

  const handleLinkPartnerSelect = (partner) => {
    setLinkPartnerSelected(partner);
    setLinkPartnerSearch(partner.legal_entity_name);
    setLinkPartnerState('confirming');
  };

  const handleLinkPartnerConfirm = async () => {
    if (!linkPartnerSelected || !processingDocId) return;
    setLinkPartnerState('submitting');
    try {
      await linkDocumentToPartner({
        document_id: processingDocId,
        partner_id: linkPartnerSelected.id,
        partner_name: linkPartnerSelected.legal_entity_name,
      });
      setProcessingStatus((prev) => ({
        ...prev,
        classification: 'partner',
        storage_area: 'crm',
        partner_name: linkPartnerSelected.legal_entity_name,
      }));
      setLinkPartnerState('done');
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to link document. Please try again.';
      setCrmFormError(msg);
      setLinkPartnerState('idle');
    }
  };

  const handleLinkPartnerCreateNew = () => {
    setCrmFormData({
      legal_entity_name: linkPartnerSearch.trim(),
      trading_short_name: '',
    });
    setCrmFormError('');
    setCrmPromptState('form');
    setLinkPartnerState('idle');
  };

  // Open in-app preview for a document (pdf -> iframe, image -> img, else fallback)
  const handlePreview = async (doc) => {
    const filename = doc.original_filename || doc.title || '';
    const ext = filename.split('.').pop().toLowerCase();
    const isText = TEXT_EXTENSIONS.includes(ext);

    // Unsupported types open the modal with a download fallback (no fetch needed)
    if (ext !== 'pdf' && !IMAGE_MIME_TYPES[ext] && !isText) {
      setPreviewDoc({ id: doc.id, name: filename, mimeType: 'other', url: null });
      return;
    }

    // Open the modal immediately in a loading state so it reacts instantly
    const mimeType = ext === 'pdf' ? 'application/pdf' : (isText ? 'text' : 'image');
    setPreviewDoc({ id: doc.id, name: filename, mimeType, url: null, loading: true });

    try {
      const blobData = await downloadDocument(doc.id);

      // Plain-text files (incl. manual notes) render inline as text
      if (isText) {
        const textContent = await new Blob([blobData]).text();
        setPreviewDoc((prev) => {
          if (prev && prev.id === doc.id && prev.loading) {
            return { ...prev, textContent, loading: false };
          }
          return prev;
        });
        return;
      }

      // Re-type the blob so the browser renders it instead of downloading it
      const contentType = ext === 'pdf' ? 'application/pdf' : IMAGE_MIME_TYPES[ext];
      const url = URL.createObjectURL(new Blob([blobData], { type: contentType }));

      // Only populate if the modal is still open for this doc; if the user
      // closed it mid-fetch, release the blob URL instead of leaking it
      setPreviewDoc((prev) => {
        if (prev && prev.id === doc.id && prev.loading) {
          return { ...prev, url, loading: false };
        }
        URL.revokeObjectURL(url);
        return prev;
      });
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewDoc((prev) => (prev && prev.id === doc.id ? null : prev));
    }
  };

  // Close preview on Escape; revoke object URL on close and unmount
  useEffect(() => {
    if (!previewDoc) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPreviewDoc(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const url = previewDoc.url;
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [previewDoc]);

  const handleDownloadDoc = async (doc, e) => {
    if (e) e.stopPropagation();
    try {
      const blob = await downloadDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || doc.title || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const completedDocs = documents.filter((d) => d.processing_status === 'completed');

  const filteredAttachDocs = attachSearch.trim()
    ? completedDocs.filter((doc) => {
        const q = attachSearch.toLowerCase();
        return (
          doc.original_filename?.toLowerCase().includes(q) ||
          doc.title?.toLowerCase().includes(q) ||
          doc.partner_name?.toLowerCase().includes(q)
        );
      })
    : completedDocs;

  const toggleAttachDoc = (doc) => {
    setAttachedDocs((prev) => {
      const exists = prev.find((d) => d.id === doc.id);
      if (exists) return prev.filter((d) => d.id !== doc.id);
      return [...prev, { id: doc.id, title: doc.original_filename || doc.title }];
    });
  };

  const removeAttachedDoc = (docId) => {
    setAttachedDocs((prev) => prev.filter((d) => d.id !== docId));
  };


  // Chat handling
  const handleSendMessage = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    const docIds = attachedDocs.map((d) => d.id);
    const docNames = attachedDocs.map((d) => d.title);
    setChatInput('');
    setAttachedDocs([]);
    setChatMessages((prev) => [
      ...prev,
      { role: 'user', content: msg, attachedDocNames: docNames.length > 0 ? docNames : undefined },
    ]);
    setChatLoading(true);

    try {
      const partnerFilter = browserPath[0] === 'crm' && browserPath.length === 2 ? browserPath[1].partnerId : null;
      const classFilter = browserPath[0] === 'general' && browserPath.length === 2 ? browserPath[1] : null;
      const result = await knowledgeHubService.sendChatMessage(
        msg,
        currentSession,
        partnerFilter,
        classFilter,
        docIds.length > 0 ? docIds : null
      );
      setCurrentSession(result.session_id);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: result.response, sources: result.sources },
      ]);
    } catch (err) {
      console.error('Chat error:', err);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, an error occurred. Please try again.' },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const startNewChat = () => {
    setCurrentSession(null);
    setChatMessages([]);
    setChatInput('');
  };

  const loadChatSession = async (sessionId) => {
    try {
      const session = await knowledgeHubService.getChatSession(sessionId);
      setCurrentSession(session.id);
      setChatMessages(session.messages || []);
    } catch (err) {
      console.error('Failed to load chat session:', err);
    }
  };

  // Read a failed document again and show its progress window, as an upload does.
  const handleReadAgain = async (doc, e) => {
    e.stopPropagation();
    setReadAgainError(null);
    setReadAgainBusyId(doc.id);
    try {
      await knowledgeHubService.readDocumentAgain(doc.id);
      resetCrmPrompt();
      setEntryFlow(false);
      setProcessingStep(0);
      setProcessingDone(false);
      setProcessingStatus({ status: 'pending' });
      setProcessingDocId(doc.id);
      setProcessingRun((n) => n + 1);
      announceReadingStarted();
      setShowUploadModal(true);
      loadData();
    } catch (err) {
      setReadAgainError({ id: doc.id, message: extractApiError(err, 'Could not start reading it again.') });
    } finally {
      setReadAgainBusyId(null);
    }
  };

  const renderFailedRow = (doc) => {
    if (doc.processing_status !== 'failed') return null;
    return (
      <div className="kh-doc-failed-row">
        {doc.can_read_again ? (
          <button
            className="kh-read-again-btn"
            onClick={(e) => handleReadAgain(doc, e)}
            disabled={readAgainBusyId === doc.id}
          >
            <FontAwesomeIcon icon={readAgainBusyId === doc.id ? faSpinner : faRedo} spin={readAgainBusyId === doc.id} />
            Try again
          </button>
        ) : (
          <span className="kh-doc-failed-hint">
            Could not be read, and the file wasn&rsquo;t kept. Delete it and upload the file again.
          </span>
        )}
        {readAgainError?.id === doc.id && (
          <span className="kh-doc-failed-hint">{readAgainError.message}</span>
        )}
      </div>
    );
  };

  const handleDeleteDocument = (doc, e) => {
    e.stopPropagation();
    setDeleteConfirm({ id: doc.id, name: doc.original_filename || doc.title });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await knowledgeHubService.deleteDocument(deleteConfirm.id);
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  const {
    crmDocs,
    generalDocs,
    partners,
    partnerCount,
    generalFolders,
    unsortedFailed,
    unsortedReading,
  } = buildFolders(documents);
  const unsortedCount = generalFolders.get(UNSORTED).length;
  const stillReadingCount = documents.filter(isStillReading).length;

  const navigateTo = (path) => setBrowserPath(path);

  return (
    <HMSNavbarWrapper>
      <div className="knowledge-hub-layout">
        <div className="knowledge-hub-header">
          <img src={logo} alt="IMT" className="header-logo-img" />
          <div>
            <h1 className="hms-screen-title">Knowledge Hub</h1>
            <p className="kh-subtitle">AI-powered document intelligence and retrieval</p>
          </div>
          {!(loading && documents.length === 0) && (
            <div className="kh-header-stats">
              <div className="kh-stat-pill">
                <FontAwesomeIcon icon={faFile} />
                <span className="kh-stat-num">{documents.length}</span>
                <span className="kh-stat-lbl">Docs</span>
              </div>
              <div className="kh-stat-pill">
                <FontAwesomeIcon icon={faGlobe} />
                <span className="kh-stat-num">{generalDocs.length}</span>
                <span className="kh-stat-lbl">General</span>
              </div>
              <div className="kh-stat-pill">
                <FontAwesomeIcon icon={faAddressBook} />
                <span className="kh-stat-num">{partnerCount}</span>
                <span className="kh-stat-lbl">Partners</span>
              </div>
              {stillReadingCount > 0 && (
                <div className="kh-stat-pill kh-stat-processing">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span className="kh-stat-num">{stillReadingCount}</span>
                  <span className="kh-stat-lbl">Processing</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="knowledge-hub-content">
          {/* Left Panel: Document Browser */}
          <div className="kh-browser-panel">
            <div className="kh-browser-toolbar">
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {isAtDocLevel && (
                <div className="kh-view-toggle">
                  <button
                    className={viewMode === 'list' ? 'active' : ''}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >
                    <FontAwesomeIcon icon={faList} />
                  </button>
                  <button
                    className={viewMode === 'grid' ? 'active' : ''}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >
                    <FontAwesomeIcon icon={faThLarge} />
                  </button>
                </div>
              )}
              <button className="kh-upload-btn" onClick={() => { setEntryFlow(false); setShowUploadModal(true); }}>
                <FontAwesomeIcon icon={faUpload} />
                Upload
              </button>
              <button className="kh-upload-btn" onClick={() => setShowNewEntryModal(true)}>
                <FontAwesomeIcon icon={faPlus} />
                New Entry
              </button>
            </div>

            {/* Breadcrumb */}
            <div className="kh-breadcrumb">
              <span
                className={`kh-breadcrumb-seg ${browserPath.length === 0 ? 'current' : 'clickable'}`}
                onClick={() => navigateTo([])}
              >
                <FontAwesomeIcon icon={faHome} />
                All Documents
              </span>
              {browserPath.length >= 1 && (
                <>
                  <FontAwesomeIcon icon={faChevronRight} className="kh-breadcrumb-sep" />
                  <span
                    className={`kh-breadcrumb-seg ${browserPath.length === 1 ? 'current' : 'clickable'}`}
                    onClick={() => navigateTo([browserPath[0]])}
                  >
                    <FontAwesomeIcon icon={browserPath[0] === 'crm' ? faBuilding : faGlobe} />
                    {browserPath[0] === 'crm' ? 'CRM' : 'General'}
                  </span>
                </>
              )}
              {browserPath.length >= 2 && (
                <>
                  <FontAwesomeIcon icon={faChevronRight} className="kh-breadcrumb-sep" />
                  <span className="kh-breadcrumb-seg current">
                    {browserPath[0] === 'crm'
                      ? browserPath[1].partnerName
                      : GENERAL_FOLDER_META[browserPath[1]]?.label || browserPath[1]}
                  </span>
                </>
              )}
            </div>

            {/* Content Area */}
            <div className="kh-browser-content">
              {/* Root: show CRM + General folder cards */}
              {browserPath.length === 0 && !debouncedSearch && (
                <div className="kh-folder-grid">
                  <div className="kh-folder-card" onClick={() => navigateTo(['crm'])}>
                    <div className="kh-folder-icon-wrap crm">
                      <FontAwesomeIcon icon={faBuilding} />
                    </div>
                    <div className="kh-folder-info">
                      <div className="kh-folder-name">CRM Partners</div>
                      <div className="kh-folder-meta">{partnerCount} partners / {crmDocs.length} docs</div>
                    </div>
                    <FontAwesomeIcon icon={faChevronRight} className="kh-folder-arrow" />
                  </div>
                  <div className="kh-folder-card" onClick={() => navigateTo(['general'])}>
                    <div className="kh-folder-icon-wrap general">
                      <FontAwesomeIcon icon={faGlobe} />
                    </div>
                    <div className="kh-folder-info">
                      <div className="kh-folder-name">General</div>
                      <div className="kh-folder-meta">
                        {CATEGORY_KEYS.length} categories / {generalDocs.length} docs
                        {unsortedCount > 0 && ` (${unsortedCount} not sorted yet)`}
                      </div>
                    </div>
                    <FontAwesomeIcon icon={faChevronRight} className="kh-folder-arrow" />
                  </div>
                </div>
              )}

              {/* CRM level: show partner folder cards */}
              {browserPath.length === 1 && browserPath[0] === 'crm' && !debouncedSearch && (
                <div className="kh-folder-grid">
                  {partners.length === 0 ? (
                    <div className="kh-empty-state">
                      <FontAwesomeIcon icon={faFolder} className="kh-empty-icon" />
                      <span>No CRM partners with documents yet</span>
                    </div>
                  ) : (
                    partners.map((p) => (
                      <div
                        key={p.partnerId}
                        className="kh-folder-card"
                        onClick={() => navigateTo(['crm', { partnerId: p.partnerId, partnerName: p.partnerName }])}
                      >
                        <div className="kh-folder-icon-wrap partner">
                          <FontAwesomeIcon icon={faAddressBook} />
                        </div>
                        <div className="kh-folder-info">
                          <div className="kh-folder-name">{p.partnerName}</div>
                          <div className="kh-folder-meta">{p.docs.length} document{p.docs.length !== 1 ? 's' : ''}</div>
                        </div>
                        <FontAwesomeIcon icon={faChevronRight} className="kh-folder-arrow" />
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* General level: show classification folder cards */}
              {browserPath.length === 1 && browserPath[0] === 'general' && !debouncedSearch && (
                <div className="kh-folder-grid">
                  {Object.entries(GENERAL_FOLDER_META).map(([key, meta]) => {
                    const count = generalFolders.get(key).length;
                    // The not-sorted folder only appears when something is in it.
                    if (key === UNSORTED && count === 0) return null;
                    const notSortedWhy = [
                      unsortedFailed > 0 && `${unsortedFailed} could not be read`,
                      unsortedReading > 0 && `${unsortedReading} still being read`,
                    ].filter(Boolean).join(', ');
                    return (
                      <div key={key} className="kh-folder-card" onClick={() => navigateTo(['general', key])}>
                        <div className={`kh-folder-icon-wrap ${key}`}>
                          <FontAwesomeIcon icon={meta.icon} />
                        </div>
                        <div className="kh-folder-info">
                          <div className="kh-folder-name">{meta.label}</div>
                          <div className="kh-folder-meta">
                            {count} document{count !== 1 ? 's' : ''}
                            {key === UNSORTED && notSortedWhy && ` · ${notSortedWhy}`}
                          </div>
                        </div>
                        <FontAwesomeIcon icon={faChevronRight} className="kh-folder-arrow" />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Document level OR search active: show documents */}
              {(isAtDocLevel || debouncedSearch) && (
                <>
                  <div className="kh-doc-list-header">
                    {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
                  </div>
                  {filteredDocs.length === 0 ? (
                    <div className="kh-empty-state">
                      <FontAwesomeIcon icon={faFileAlt} className="kh-empty-icon" />
                      <span>No documents found</span>
                    </div>
                  ) : viewMode === 'list' ? (
                    <div className="kh-doc-list">
                      {filteredDocs.map((doc) => {
                        const aiDisplayName = doc.docuware_document_id
                          ? knowledgeDisplayNames?.[String(doc.docuware_document_id)]?.display_name
                          : null;
                        // A failed read is not "generating" anything; its failed tag says so.
                        const isProcessing = isStillReading(doc);
                        const renderName = aiDisplayName || (isProcessing ? `${doc.original_filename || doc.title} (generating name...)` : (doc.title || doc.original_filename));
                        return (
                        <div key={doc.id} className="kh-doc-item">
                          <FontAwesomeIcon icon={faFile} className="kh-doc-icon" />
                          <div className="kh-doc-info">
                            <div className="kh-doc-title" title={renderName}>{renderName}</div>
                            {doc.ai_summary && (
                              <div className="kh-doc-summary">{doc.ai_summary}</div>
                            )}
                            <div className="kh-doc-meta">
                              {doc.classification && (
                                <span className={`kh-classification-tag ${doc.classification}`}>
                                  {doc.classification}
                                </span>
                              )}
                              {doc.processing_status !== 'completed' && (
                                <span className={`kh-doc-status ${doc.processing_status}`}>
                                  {doc.processing_status}
                                </span>
                              )}
                              <span className="kh-doc-date">{formatDate(doc.created_at)}</span>
                              {doc.uploaded_by_name && (
                                <span className="kh-doc-date">Uploaded by {doc.uploaded_by_name}</span>
                              )}
                              {doc.source_hint && (
                                <span className="kh-doc-date">Source: {doc.source_hint}</span>
                              )}
                            </div>
                            {renderFailedRow(doc)}
                          </div>
                          <div className="kh-doc-actions">
                            {doc.docuware_document_id && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); handlePreview(doc); }} title="Preview">
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                                <button onClick={(e) => handleDownloadDoc(doc, e)} title="Download">
                                  <FontAwesomeIcon icon={faDownload} />
                                </button>
                              </>
                            )}
                            <button onClick={(e) => handleDeleteDocument(doc, e)} title="Delete">
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="kh-doc-grid">
                      {filteredDocs.map((doc) => {
                        const aiDisplayName = doc.docuware_document_id
                          ? knowledgeDisplayNames?.[String(doc.docuware_document_id)]?.display_name
                          : null;
                        // A failed read is not "generating" anything; its failed tag says so.
                        const isProcessing = isStillReading(doc);
                        const renderName = aiDisplayName || (isProcessing ? `${doc.original_filename || doc.title} (generating name...)` : (doc.title || doc.original_filename));
                        return (
                        <div key={doc.id} className="kh-doc-grid-card">
                          <div className="kh-doc-grid-icon">
                            <FontAwesomeIcon icon={faFile} />
                          </div>
                          <div className="kh-doc-grid-title" title={renderName}>{renderName}</div>
                          <div className="kh-doc-grid-meta">
                            {doc.classification && (
                              <span className={`kh-classification-tag ${doc.classification}`}>
                                {doc.classification}
                              </span>
                            )}
                            <span className="kh-doc-date">{formatDate(doc.created_at)}</span>
                            {doc.uploaded_by_name && (
                              <span className="kh-doc-date">Uploaded by {doc.uploaded_by_name}</span>
                            )}
                            {doc.source_hint && (
                              <span className="kh-doc-date">Source: {doc.source_hint}</span>
                            )}
                          </div>
                          {renderFailedRow(doc)}
                          <div className="kh-doc-actions">
                            {doc.docuware_document_id && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); handlePreview(doc); }} title="Preview">
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                                <button onClick={(e) => handleDownloadDoc(doc, e)} title="Download">
                                  <FontAwesomeIcon icon={faDownload} />
                                </button>
                              </>
                            )}
                            <button onClick={(e) => handleDeleteDocument(doc, e)} title="Delete">
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Non-doc level with no search and no folders to show (shouldn't happen, but safety) */}
              {!isAtDocLevel && !debouncedSearch && browserPath.length >= 2 && (
                <div className="kh-empty-state">
                  <FontAwesomeIcon icon={faFolder} className="kh-empty-icon" />
                  <span>Nothing to display</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel: Chat Interface */}
          <div className="kh-chat-panel">
            <div className="kh-chat-header">
              <FontAwesomeIcon icon={faRobot} className="kh-chat-header-icon" />
              <span className="kh-chat-header-title">Knowledge Hub AI</span>
              <button className="kh-new-chat-btn" onClick={startNewChat}>
                <FontAwesomeIcon icon={faPlus} />
                New Chat
              </button>
            </div>

            <div className="kh-chat-messages" ref={chatMessagesRef}>
              {chatMessages.length === 0 ? (
                <div className="kh-chat-empty">
                  <FontAwesomeIcon icon={faComments} className="kh-empty-icon" />
                  <h3>Ask the Knowledge Hub</h3>
                  <p>
                    Ask questions about your uploaded documents. The AI will search through your
                    knowledge base and provide answers with source citations.
                  </p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div key={idx} className={`kh-message ${msg.role}`}>
                    <div className="kh-message-avatar">
                      <FontAwesomeIcon icon={msg.role === 'user' ? faUser : faRobot} />
                    </div>
                    <div>
                      {msg.attachedDocNames?.length > 0 && (
                        <div className="kh-message-attached-docs">
                          <FontAwesomeIcon icon={faPaperclip} />
                          {msg.attachedDocNames.map((name, didx) => (
                            <span key={didx} className="kh-attached-doc-name">{name}</span>
                          ))}
                        </div>
                      )}
                      <div className="kh-message-bubble">
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                      {msg.sources?.length > 0 && (
                        <div className="kh-message-sources">
                          {msg.sources.map((src, sidx) => (
                            <span key={sidx} className="kh-source-tag">
                              <FontAwesomeIcon icon={faFile} />
                              {src.title}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {chatLoading && (
                <div className="kh-loading-indicator">
                  <div className="kh-loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  Searching knowledge base...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="kh-chat-input-wrap">
              {attachedDocs.length > 0 && (
                <div className="kh-attached-pills">
                  {attachedDocs.map((doc) => (
                    <span key={doc.id} className="kh-attached-pill">
                      <FontAwesomeIcon icon={faFile} />
                      <span className="kh-attached-pill-name">{doc.title}</span>
                      <button onClick={() => removeAttachedDoc(doc.id)} className="kh-attached-pill-remove">
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="kh-chat-input-area">
                <button
                  className="kh-attach-btn"
                  onClick={() => setShowAttachModal(true)}
                  title="Attach documents to message"
                  disabled={chatLoading}
                >
                  <FontAwesomeIcon icon={faPaperclip} />
                </button>
                <input
                  type="text"
                  placeholder="Ask a question about your documents..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  disabled={chatLoading}
                />
                <button
                  className="kh-send-btn"
                  onClick={handleSendMessage}
                  disabled={chatLoading || !chatInput.trim()}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="kh-modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
            <div className="kh-modal" onClick={(e) => e.stopPropagation()}>
              <h2>{entryFlow ? 'New Knowledge Entry' : 'Upload to Knowledge Hub'}</h2>

              {!processingDocId && !processingDone ? (
                <>
                  <FileDropZone
                    className="kh-dropzone"
                    onFiles={(files) => takeFile(files[0], setUploadFile)}
                    onError={setDropError}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FontAwesomeIcon icon={faCloudUploadAlt} className="kh-dropzone-icon" />
                    <p>
                      Drag and drop a file here, or{' '}
                      <span className="kh-browse-link">browse</span>
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Maximum file size: 50MB</p>
                  </FileDropZone>
                  {dropError && (
                    <div className="kh-drop-error" role="alert">{dropError}</div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={(e) => {
                      takeFile(e.target.files?.[0], setUploadFile);
                      e.target.value = '';
                    }}
                  />

                  {uploadFile && (
                    <div className="kh-modal-file-info">
                      <FontAwesomeIcon icon={faFile} className="kh-modal-file-icon" />
                      <span className="kh-modal-file-name">{uploadFile.name}</span>
                      <span className="kh-modal-file-size">{formatFileSize(uploadFile.size)}</span>
                      <button className="kh-modal-file-remove" onClick={() => setUploadFile(null)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}

                  <label className="kh-crm-label kh-classification-field">
                    Classification
                    <select
                      className="kh-crm-input"
                      value={uploadClassification}
                      onChange={(e) => setUploadClassification(e.target.value)}
                      disabled={uploading}
                    >
                      <option value="">Let AI decide</option>
                      {Object.entries(CLASSIFICATION_META).map(([key, meta]) => (
                        <option key={key} value={key}>{meta.label}</option>
                      ))}
                    </select>
                    <span className="kh-classification-hint">
                      Sets the name tag: PROD / MARKT / CRM / GEN
                    </span>
                  </label>

                  <div className="kh-modal-actions">
                    <button
                      className="kh-modal-cancel"
                      onClick={() => {
                        setShowUploadModal(false);
                        setUploadFile(null);
                        setUploadClassification('');
                      }}
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                    <button
                      className="kh-modal-submit"
                      onClick={handleUpload}
                      disabled={!uploadFile || uploading}
                    >
                      {uploading ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin /> Uploading...
                        </>
                      ) : (
                        'Upload & Process'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {processingStep === -1 ? (
                    /* Failed state */
                    <div className="kh-processing-status failed">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <div>
                        <div className="kh-ps-title">Processing failed</div>
                        <div className="kh-ps-detail">
                          {processingStatus?.error || 'Something went wrong. Please try again or contact support.'}
                        </div>
                      </div>
                    </div>
                  ) : processingStep === 4 ? (
                    /* Completed state */
                    <>
                      <div className="kh-processing-status completed">
                        <FontAwesomeIcon icon={faCheckCircle} className="kh-ps-done-icon" />
                        <div>
                          <div className="kh-ps-title">{entryFlow ? 'Your entry is ready' : 'Your document is ready'}</div>
                          <div className="kh-ps-detail">
                            {processingStatus?.classification && (
                              <>
                                Filed under{' '}
                                <span className={`kh-classification-tag ${processingStatus.classification}`}>
                                  {processingStatus.classification}
                                </span>
                                {processingStatus.storage_area === 'crm' && processingStatus.partner_name && (
                                  <> &mdash; linked to <strong>{processingStatus.partner_name}</strong></>
                                )}
                              </>
                            )}
                            {processingStatus?.ai_summary && (
                              <div className="kh-ps-summary">{processingStatus.ai_summary}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Link-to-CRM correction widget -- shown for any doc in general storage */}
                      {processingStatus?.storage_area === 'general' &&
                        linkPartnerState !== 'done' &&
                        crmPromptState !== 'form' &&
                        crmPromptState !== 'submitting' &&
                        crmPromptState !== 'error' &&
                        crmPromptState !== 'done' && (
                        <div className="kh-crm-form" ref={linkPartnerDropdownRef}>
                          <div className="kh-crm-form-title">
                            <FontAwesomeIcon icon={faSearch} />
                            Correction
                          </div>
                          <p className="kh-crm-link-hint">
                            If this {entryFlow ? 'entry' : 'file'} should be associated with a CRM profile, search for
                            a partner below to link it, or create a new one.
                          </p>
                          <div className="kh-crm-link-search-wrap">
                            <input
                              className="kh-crm-input"
                              type="text"
                              value={linkPartnerSearch}
                              onChange={(e) => {
                                setLinkPartnerSearch(e.target.value);
                                setLinkPartnerSelected(null);
                                setLinkPartnerState('searching');
                              }}
                              onFocus={() => {
                                if (linkPartnerSearch.trim().length >= 2) {
                                  setLinkPartnerState('searching');
                                }
                              }}
                              placeholder="Search CRM partners by name..."
                              disabled={linkPartnerState === 'submitting'}
                            />
                            {linkPartnerState === 'searching' &&
                              linkPartnerDebouncedSearch.trim().length >= 2 && (
                              <div className="kh-crm-link-dropdown">
                                {linkPartnerLoading ? (
                                  <div className="kh-crm-link-dropdown-msg">
                                    <FontAwesomeIcon icon={faSpinner} spin /> Searching...
                                  </div>
                                ) : linkPartnerResults.length > 0 ? (
                                  <>
                                    {linkPartnerResults.map((partner) => (
                                      <div
                                        key={partner.id}
                                        className="kh-crm-link-dropdown-item"
                                        onClick={() => handleLinkPartnerSelect(partner)}
                                      >
                                        <div className="kh-crm-link-dropdown-name">
                                          {partner.legal_entity_name}
                                        </div>
                                        {partner.trading_short_name && (
                                          <div className="kh-crm-link-dropdown-sub">
                                            Trading as: {partner.trading_short_name}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    <div
                                      className="kh-crm-link-dropdown-item kh-crm-link-create-new"
                                      onClick={handleLinkPartnerCreateNew}
                                    >
                                      <FontAwesomeIcon icon={faPlus} /> Create new CRM partner
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="kh-crm-link-dropdown-msg">
                                      No approved partners found for &ldquo;{linkPartnerDebouncedSearch}&rdquo;
                                    </div>
                                    <div
                                      className="kh-crm-link-dropdown-item kh-crm-link-create-new"
                                      onClick={handleLinkPartnerCreateNew}
                                    >
                                      <FontAwesomeIcon icon={faPlus} /> Create new CRM partner
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          {linkPartnerSearch.trim().length > 0 &&
                            linkPartnerSearch.trim().length < 2 && (
                            <span className="kh-crm-link-hint-min">
                              Type at least 2 characters to search
                            </span>
                          )}
                          {linkPartnerState === 'confirming' && linkPartnerSelected && (
                            <div className="kh-crm-link-confirm">
                              <span>
                                Link to <strong>{linkPartnerSelected.legal_entity_name}</strong>?
                              </span>
                              <div className="kh-crm-form-actions">
                                <button
                                  className="kh-btn-secondary"
                                  onClick={() => {
                                    setLinkPartnerSelected(null);
                                    setLinkPartnerState('idle');
                                    setLinkPartnerSearch('');
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="kh-btn-primary"
                                  onClick={handleLinkPartnerConfirm}
                                >
                                  Confirm
                                </button>
                              </div>
                            </div>
                          )}
                          {linkPartnerState === 'submitting' && (
                            <div className="kh-crm-link-confirm">
                              <FontAwesomeIcon icon={faSpinner} spin /> Linking document...
                            </div>
                          )}
                          {crmFormError && linkPartnerState === 'idle' && (
                            <div className="kh-crm-form-error">
                              <FontAwesomeIcon icon={faExclamationTriangle} /> {crmFormError}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Link-partner success note */}
                      {linkPartnerState === 'done' && crmPromptState !== 'done' && (
                        <div className="kh-crm-prompt kh-crm-success">
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <span>
                            Document linked to <strong>{linkPartnerSelected?.legal_entity_name || processingStatus?.partner_name}</strong> and
                            moved to the CRM folder.
                          </span>
                        </div>
                      )}

                      {/* CRM partner prompt for unmatched partner documents */}
                      {crmPromptState === 'prompt' && (
                        <div className="kh-crm-prompt">
                          <div className="kh-crm-prompt-body">
                            <FontAwesomeIcon icon={faBuilding} className="kh-crm-prompt-icon" />
                            <div className="kh-crm-prompt-text">
                              <strong>{processingStatus.partner_name}</strong> was not found in the CRM.
                              Would you like to add them as a new CRM partner?
                            </div>
                          </div>
                          <div className="kh-crm-prompt-actions">
                            <button
                              className="kh-btn-secondary"
                              onClick={() => setCrmPromptState('idle')}
                            >
                              No, keep in General
                            </button>
                            <button
                              className="kh-btn-primary"
                              onClick={() => setCrmPromptState('form')}
                            >
                              Yes, add to CRM
                            </button>
                          </div>
                        </div>
                      )}

                      {(crmPromptState === 'form' || crmPromptState === 'submitting' || crmPromptState === 'error') && (
                        <div className="kh-crm-form">
                          <div className="kh-crm-form-title">
                            <FontAwesomeIcon icon={faBuilding} />
                            New CRM Partner
                          </div>
                          <div className="kh-crm-form-fields">
                            <label className="kh-crm-label">
                              Legal Entity Name <span className="kh-required">*</span>
                              <input
                                className="kh-crm-input"
                                type="text"
                                value={crmFormData.legal_entity_name}
                                onChange={(e) =>
                                  setCrmFormData((p) => ({ ...p, legal_entity_name: e.target.value }))
                                }
                                disabled={crmPromptState === 'submitting'}
                                placeholder="Full registered company name"
                              />
                            </label>
                            <label className="kh-crm-label">
                              Trading / Short Name
                              <input
                                className="kh-crm-input"
                                type="text"
                                value={crmFormData.trading_short_name}
                                onChange={(e) =>
                                  setCrmFormData((p) => ({ ...p, trading_short_name: e.target.value }))
                                }
                                disabled={crmPromptState === 'submitting'}
                                placeholder="Common name or abbreviation (optional)"
                              />
                            </label>
                          </div>
                          {crmFormError && (
                            <div className="kh-crm-form-error">
                              <FontAwesomeIcon icon={faExclamationTriangle} /> {crmFormError}
                            </div>
                          )}
                          <div className="kh-crm-form-actions">
                            <button
                              className="kh-btn-secondary"
                              onClick={() => {
                                if (processingStatus?.storage_area === 'general' || linkPartnerSearch) {
                                  setCrmPromptState('idle');
                                } else {
                                  setCrmPromptState('prompt');
                                }
                              }}
                              disabled={crmPromptState === 'submitting'}
                            >
                              Back
                            </button>
                            <button
                              className="kh-btn-primary"
                              onClick={handleCrmFormSubmit}
                              disabled={crmPromptState === 'submitting'}
                            >
                              {crmPromptState === 'submitting' ? (
                                <><FontAwesomeIcon icon={faSpinner} spin /> Creating&hellip;</>
                              ) : (
                                'Create CRM Partner'
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      {crmPromptState === 'done' && (
                        <div className="kh-crm-prompt kh-crm-success">
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <span>
                            <strong>{crmFormData.legal_entity_name}</strong> added to CRM and document
                            moved to the CRM folder.
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    /* In-progress steps */
                    <div className="kh-processing-steps">
                      <div className="kh-ps-header">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Processing your document&hellip;</span>
                      </div>
                      <ul className="kh-ps-steps">
                        {[
                          { icon: faCloudUploadAlt, label: 'Sending file to Knowledge Hub', detail: 'Securely transferring your document' },
                          { icon: faFile,          label: 'Reading your document',          detail: 'Extracting all text and content' },
                          { icon: faBrain,         label: 'Analysing the content',          detail: 'Classifying type and identifying key topics' },
                          { icon: faSearch,        label: 'Indexing for search',            detail: 'Making it searchable by the AI assistant' },
                        ].map((step, i) => {
                          const done = processingStep > i;
                          const active = processingStep === i;
                          return (
                            <li key={i} className={`kh-ps-step ${done ? 'done' : active ? 'active' : 'waiting'}`}>
                              <span className="kh-ps-step-icon">
                                {done
                                  ? <FontAwesomeIcon icon={faCheckCircle} />
                                  : active
                                  ? <FontAwesomeIcon icon={faSpinner} spin />
                                  : <span className="kh-ps-step-num">{i + 1}</span>
                                }
                              </span>
                              <span className="kh-ps-step-text">
                                <span className="kh-ps-step-label">{step.label}</span>
                                {active && <span className="kh-ps-step-detail">{step.detail}</span>}
                                {active && i >= 1 && (
                                  <span className="kh-ps-step-progress">
                                    <ReadingProgress
                                      compact
                                      showStage={false}
                                      progress={processingStatus?.progress}
                                      leaveText="You can close this window — it carries on and the document appears in the list when it is ready."
                                    />
                                  </span>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {(processingStep === 4 || processingStep === -1) && (
                    <div className="kh-modal-actions">
                      <button
                        className="kh-modal-submit"
                        onClick={() => {
                          setShowUploadModal(false);
                          setProcessingDocId(null);
                          setProcessingStatus(null);
                          setProcessingStep(0);
                          setProcessingDone(false);
                          setUploadClassification('');
                          resetCrmPrompt();
                        }}
                      >
                        {processingStep === 4 ? 'Done' : 'Close'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        {/* New Entry Modal */}
        {showNewEntryModal && (
          <div className="kh-modal-overlay" onClick={() => !creatingEntry && closeNewEntryModal()}>
            <div className="kh-modal" onClick={(e) => e.stopPropagation()}>
              <h2>New Knowledge Entry</h2>

              <textarea
                className="kh-new-entry-textarea"
                placeholder="Write or paste your note..."
                value={newEntryText}
                onChange={(e) => setNewEntryText(e.target.value)}
                disabled={creatingEntry}
              />

              <FileDropZone
                className="kh-dropzone kh-new-entry-dropzone"
                onFiles={(files) => takeFile(files[0], setNewEntryFile)}
                onError={setDropError}
                onClick={() => newEntryFileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faPaperclip} className="kh-dropzone-icon" />
                <p>
                  Attach a file (optional) &mdash; drag and drop, or{' '}
                  <span className="kh-browse-link">browse</span>
                </p>
              </FileDropZone>
              {dropError && (
                <div className="kh-drop-error" role="alert">{dropError}</div>
              )}
              <input
                ref={newEntryFileInputRef}
                type="file"
                hidden
                onChange={(e) => {
                  takeFile(e.target.files?.[0], setNewEntryFile);
                  e.target.value = '';
                }}
              />

              {newEntryFile && (
                <div className="kh-modal-file-info">
                  <FontAwesomeIcon icon={faFile} className="kh-modal-file-icon" />
                  <span className="kh-modal-file-name">{newEntryFile.name}</span>
                  <span className="kh-modal-file-size">{formatFileSize(newEntryFile.size)}</span>
                  <button className="kh-modal-file-remove" onClick={() => setNewEntryFile(null)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              <label className="kh-crm-label kh-classification-field">
                Classification
                <select
                  className="kh-crm-input"
                  value={newEntryClassification}
                  onChange={(e) => setNewEntryClassification(e.target.value)}
                  disabled={creatingEntry}
                >
                  <option value="">Let AI decide</option>
                  {Object.entries(CLASSIFICATION_META).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.label}</option>
                  ))}
                </select>
                <span className="kh-classification-hint">
                  Sets the name tag: PROD / MARKT / CRM / GEN
                </span>
              </label>

              {newEntryError && (
                <div className="kh-crm-form-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> {newEntryError}
                </div>
              )}

              <div className="kh-modal-actions">
                <button
                  className="kh-modal-cancel"
                  onClick={closeNewEntryModal}
                  disabled={creatingEntry}
                >
                  Cancel
                </button>
                <button
                  className="kh-modal-submit"
                  onClick={handleCreateEntry}
                  disabled={(!newEntryText.trim() && !newEntryFile) || creatingEntry}
                >
                  {creatingEntry ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attach Document Modal */}
        {showAttachModal && (
          <div className="kh-modal-overlay" onClick={() => setShowAttachModal(false)}>
            <div className="kh-modal kh-attach-modal" onClick={(e) => e.stopPropagation()}>
              <div className="kh-attach-modal-header">
                <h2>Attach Documents</h2>
                <button className="kh-modal-close" onClick={() => setShowAttachModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="kh-attach-search-wrap">
                <FontAwesomeIcon icon={faSearch} className="kh-attach-search-icon" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={attachSearch}
                  onChange={(e) => setAttachSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="kh-attach-list">
                {filteredAttachDocs.length === 0 ? (
                  <div className="kh-empty-state" style={{ padding: '2rem' }}>
                    <FontAwesomeIcon icon={faFileAlt} className="kh-empty-icon" />
                    <span>No documents found</span>
                  </div>
                ) : (
                  filteredAttachDocs.map((doc) => {
                    const isSelected = attachedDocs.some((d) => d.id === doc.id);
                    return (
                      <div
                        key={doc.id}
                        className={`kh-attach-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleAttachDoc(doc)}
                      >
                        <div className="kh-attach-item-check">
                          {isSelected && <FontAwesomeIcon icon={faCheck} />}
                        </div>
                        <FontAwesomeIcon icon={faFile} className="kh-doc-icon" />
                        <div className="kh-attach-item-info">
                          <div className="kh-doc-title">{doc.original_filename || doc.title}</div>
                          <div className="kh-doc-meta">
                            {doc.classification && (
                              <span className={`kh-classification-tag ${doc.classification}`}>
                                {doc.classification}
                              </span>
                            )}
                            {doc.partner_name && (
                              <span className="kh-doc-date">{doc.partner_name}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="kh-attach-modal-footer">
                <span className="kh-attach-count">
                  {attachedDocs.length} document{attachedDocs.length !== 1 ? 's' : ''} selected
                </span>
                <button
                  className="kh-btn-primary"
                  onClick={() => setShowAttachModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="kh-modal-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
            <div className="kh-modal kh-delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="kh-delete-icon-wrap">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <h3 className="kh-delete-title">Delete Document</h3>
              <p className="kh-delete-message">
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This will permanently remove the document and all its indexed data.
              </p>
              <div className="kh-delete-actions">
                <button
                  className="kh-btn-secondary"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="kh-btn-danger"
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Deleting...</>
                  ) : (
                    <><FontAwesomeIcon icon={faTrash} /> Delete</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document Preview Modal */}
        {previewDoc && (
          <div className="kh-modal-overlay" onClick={() => setPreviewDoc(null)}>
            <div className="kh-modal kh-preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="kh-attach-modal-header">
                <h2>{previewDoc.name}</h2>
                <button className="kh-modal-close" onClick={() => setPreviewDoc(null)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="kh-preview-modal-body">
                {previewDoc.loading && (
                  <div className="kh-preview-unavailable">
                    <FontAwesomeIcon icon={faSpinner} spin className="kh-preview-unavailable-icon" />
                    <p>Loading preview...</p>
                  </div>
                )}
                {!previewDoc.loading && previewDoc.mimeType === 'application/pdf' && (
                  <iframe
                    src={previewDoc.url}
                    title={previewDoc.name}
                    className="kh-preview-frame"
                  />
                )}
                {!previewDoc.loading && previewDoc.mimeType === 'text' && (
                  <pre className="kh-preview-text">{previewDoc.textContent}</pre>
                )}
                {!previewDoc.loading && previewDoc.mimeType === 'image' && (
                  <img
                    src={previewDoc.url}
                    alt={previewDoc.name}
                    className="kh-preview-image"
                  />
                )}
                {previewDoc.mimeType === 'other' && (
                  <div className="kh-preview-unavailable">
                    <FontAwesomeIcon icon={faFile} className="kh-preview-unavailable-icon" />
                    <p>Preview not available for this file type</p>
                    <button
                      className="kh-btn-primary"
                      onClick={() => handleDownloadDoc({ id: previewDoc.id, original_filename: previewDoc.name })}
                    >
                      <FontAwesomeIcon icon={faDownload} /> Download
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      <Footer />
      </div>
    </HMSNavbarWrapper>
  );
}

export default withAuth(KnowledgeHub);
